// Epic 10 Showcase — the pane in the Collection tab. The owner's arranged
// public collection: the items they've opted in, in the order they chose.
//
// Interactions (all owner-facing, demo-grade):
//   * drag a collection item from the grid ONTO the pane → publishes it
//     (appends to the end); the grid's right-click menu does the same.
//   * drag an item WITHIN the pane → reorders; the arrangement is persisted
//     and is exactly what a stranger sees at /u/{handle}.
//   * the ✕ on an item → unpublishes it (takes it back down).
//
// Renders the same artifacts (PatronageCertificate / SealedDossier) the owner
// sees in the grid and a stranger sees on the public page — a show-off whose
// pane looked different from the real thing would fail at first glance.

import { useEffect, useMemo, useState, type DragEvent } from "react";
import toast from "react-hot-toast";
import {
  PatronageCertificate,
  SealedDossier,
  type SealedDossierData,
} from "@greenhold/components";

import type { PublicCollectionItem } from "../public-collection/publicCollectionTypes";
import { publicCertProps } from "../../pages/public/PublicCertificatePage";
import { useAccountMe } from "../admin/useAccountMe";
import {
  showcaseItemId,
  useReorderShowcase,
  useSetShowcaseMembership,
  useShowcase,
} from "./useShowcase";
import {
  isShowcaseDrag,
  readShowcaseDrag,
  writeShowcaseDrag,
} from "./showcaseDnd";

function sealedData(dto: Extract<PublicCollectionItem, { kind: "sealed_dossier" }>): SealedDossierData {
  return {
    id: dto.sealed_dossier_id,
    drop_id: dto.drop_id,
    edition_number: dto.edition_number,
    portfolio_title: dto.portfolio_title,
    drop_status: dto.drop_status,
    retiring_at: dto.retiring_at,
    seal_color: dto.seal_color,
  };
}

function itemLabel(item: PublicCollectionItem): string {
  return item.kind === "certificate"
    ? item.snapshot_display_name
    : item.portfolio_title;
}

export function ShowcasePane() {
  const { data, isLoading, isError } = useShowcase();
  const setMembership = useSetShowcaseMembership();
  const reorder = useReorderShowcase();
  const account = useAccountMe();
  const handle = account.data?.data.handle ?? null;
  const profilePrivate = account.data?.data.profile_visibility === "private";

  // Copy the logged-out-viewable link to the owner's public collection
  // (/u/{handle}). Needs a claimed handle; a private profile still copies but
  // warns that the page 404s for strangers until it's made public.
  function handleShare() {
    if (!handle) {
      toast.error("Claim a public handle first to get a shareable link.");
      return;
    }
    const url = `${window.location.origin}/u/${handle}`;
    const notify = () =>
      profilePrivate
        ? toast.success(
            "Link copied — your profile is private, so only you can open it until you make it public.",
          )
        : toast.success("Public showcase link copied!");
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(notify)
        .catch(() => window.prompt("Copy your showcase link:", url));
    } else {
      window.prompt("Copy your showcase link:", url);
    }
  }

  const serverItems = useMemo(() => data?.data.items ?? [], [data]);
  // A signature of the server's order, so the local drag order resyncs only
  // when the server truth actually changes (not on every render).
  const serverKey = serverItems.map((it) => showcaseItemId(it)).join(",");

  const [order, setOrder] = useState<PublicCollectionItem[]>(serverItems);
  const [dropActive, setDropActive] = useState(false);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // The item currently being inspected in the closer-look loupe (click a
  // showcase artifact to open it — same "get a closer look" affordance the
  // grid offers).
  const [loupe, setLoupe] = useState<PublicCollectionItem | null>(null);

  useEffect(() => {
    setOrder(serverItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  function persistOrder(next: PublicCollectionItem[]) {
    setOrder(next);
    reorder.mutate(
      next.map((it) => ({ kind: it.kind, id: showcaseItemId(it) })),
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Reorder failed"),
      },
    );
  }

  function moveItem(from: number, to: number) {
    if (from === to) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistOrder(next);
  }

  function addFromGrid(kind: PublicCollectionItem["kind"], id: string) {
    // Already present? No-op (the backend would 200 a no-op anyway, but this
    // avoids a needless round-trip and a confusing toast).
    if (order.some((it) => showcaseItemId(it) === id)) return;
    setMembership.mutate(
      { kind, id, state: true },
      {
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : "Couldn't add to showcase",
          ),
      },
    );
  }

  function removeItem(item: PublicCollectionItem) {
    setOrder((prev) =>
      prev.filter((it) => showcaseItemId(it) !== showcaseItemId(item)),
    );
    setMembership.mutate(
      { kind: item.kind, id: showcaseItemId(item), state: false },
      {
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : "Couldn't remove item",
          ),
      },
    );
  }

  function handleItemDrop(event: DragEvent, targetIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    setOverIndex(null);
    setDropActive(false);
    const payload = readShowcaseDrag(event);
    if (!payload) return;
    if (payload.source === "pane" && typeof payload.index === "number") {
      moveItem(payload.index, targetIndex);
    } else if (payload.source === "grid") {
      addFromGrid(payload.kind, payload.id);
    }
  }

  function handlePaneDrop(event: DragEvent) {
    event.preventDefault();
    setDropActive(false);
    setOverIndex(null);
    const payload = readShowcaseDrag(event);
    if (!payload) return;
    if (payload.source === "grid") {
      addFromGrid(payload.kind, payload.id);
    } else if (payload.source === "pane" && typeof payload.index === "number") {
      // Dropped on empty pane space → move to the end.
      moveItem(payload.index, order.length - 1);
    }
  }

  const isEmpty = order.length === 0;

  return (
    <>
    <section
      aria-label="Showcase"
      onDragOver={(e) => {
        if (isShowcaseDrag(e)) {
          e.preventDefault();
          setDropActive(true);
        }
      }}
      onDragLeave={(e) => {
        // Only clear when the pointer actually leaves the pane, not on every
        // child-boundary crossing.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDropActive(false);
        }
      }}
      onDrop={handlePaneDrop}
      className={`rounded-lg border p-4 transition-colors ${
        dropActive
          ? "border-brand-gold-400 bg-brand-forest-800/80"
          : "border-brand-gold-500/30 bg-brand-forest-800/40"
      }`}
    >
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold text-brand-gold-200">
            Showcase
          </h2>
          <p className="font-body text-[11px] opacity-60">
            The items you show off publicly, in the order you arrange. Drag an
            item here, or right-click one in your collection.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {order.length > 0 && (
            <span className="font-body text-[11px] opacity-50">
              {order.length} shown
            </span>
          )}
          <button
            type="button"
            onClick={handleShare}
            data-testid="showcase-share-link"
            className="rounded-md border border-brand-gold-500/40 px-2.5 py-1 font-body text-[11px] font-semibold text-brand-gold-200 transition-colors hover:bg-brand-forest-700/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold-400"
          >
            Share link
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="font-body text-xs opacity-60">Loading your showcase…</p>
      ) : isError ? (
        <p className="font-body text-xs text-state-error-fg opacity-80">
          Couldn't load your showcase.
        </p>
      ) : isEmpty ? (
        <div
          className={`flex min-h-[7rem] items-center justify-center rounded-md border border-dashed px-4 text-center ${
            dropActive
              ? "border-brand-gold-400 text-brand-gold-200"
              : "border-brand-forest-600 text-brand-paper-100/60"
          }`}
        >
          <p className="font-body text-xs">
            Nothing here yet. Drag a certificate or sealed dossier into this
            box, or right-click an item and choose <strong>Showcase</strong>.
          </p>
        </div>
      ) : (
        <ul className="flex flex-wrap gap-4">
          {order.map((item, index) => {
            const id = showcaseItemId(item);
            return (
              <li
                key={id}
                draggable
                onDragStart={(e) =>
                  writeShowcaseDrag(e, {
                    source: "pane",
                    kind: item.kind,
                    id,
                    index,
                  })
                }
                onDragEnd={(e) => {
                  // Dropped outside every showcase drop target (the grid, the
                  // page, anywhere that isn't the pane) → take it down. A drop
                  // handled inside the pane sets dropEffect="move"; an
                  // unhandled release leaves it "none".
                  if (e.dataTransfer.dropEffect === "none") {
                    removeItem(item);
                  }
                }}
                onDragOver={(e) => {
                  if (isShowcaseDrag(e)) {
                    e.preventDefault();
                    e.stopPropagation();
                    setOverIndex(index);
                  }
                }}
                onDrop={(e) => handleItemDrop(e, index)}
                className={`group relative flex cursor-grab flex-col items-center gap-1 rounded-md p-1 active:cursor-grabbing ${
                  overIndex === index ? "ring-2 ring-brand-gold-400" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => setLoupe(item)}
                  aria-label={`Take a closer look at ${itemLabel(item)}`}
                  className="w-24 cursor-zoom-in rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold-400"
                >
                  {item.kind === "certificate" ? (
                    <PatronageCertificate width={96} {...publicCertProps(item)} />
                  ) : (
                    <SealedDossier
                      dossier={sealedData(item)}
                      size="thumb"
                      game={item.portfolio_theme ?? undefined}
                    />
                  )}
                </button>
                <span className="w-24 truncate text-center font-body text-[10px] opacity-60">
                  {itemLabel(item)}
                </span>
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  aria-label={`Remove ${itemLabel(item)} from showcase`}
                  className="absolute right-0 top-0 hidden h-5 w-5 items-center justify-center rounded-full bg-state-error-fill text-[11px] text-state-error-fg shadow group-hover:flex"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
    {loupe && <ShowcaseLoupe item={loupe} onClose={() => setLoupe(null)} />}
    </>
  );
}

/// Closer-look overlay for a showcase artifact — a near-fullscreen render of
/// the same certificate / sealed dossier, on a dark backdrop. Backdrop click
/// or Escape closes. Read-only: this is inspection, not the owner action
/// dialog (the grid already hosts those).
function ShowcaseLoupe({
  item,
  onClose,
}: {
  item: PublicCollectionItem;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Largest cert width (320×452) that fits ~90% of the viewport in both axes.
  const certWidth = Math.max(
    240,
    Math.min(window.innerWidth * 0.9, window.innerHeight * 0.9 * (320 / 452)),
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${itemLabel(item)}, enlarged`}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(6, 15, 11, 0.92)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col items-center gap-3"
      >
        {item.kind === "certificate" ? (
          <PatronageCertificate width={certWidth} {...publicCertProps(item)} />
        ) : (
          <SealedDossier
            dossier={sealedData(item)}
            size="hero"
            game={item.portfolio_theme ?? undefined}
          />
        )}
        <span className="text-center font-body text-sm text-brand-paper-100/80">
          {itemLabel(item)}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close closer look"
        className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-xl text-brand-paper-100 opacity-80 hover:bg-white/10 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold-400"
      >
        ✕
      </button>
    </div>
  );
}
